import FileSaver from 'file-saver';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useExportConversationsForReportsToExcel } from '../api/collaboration/collaboration';
import { ensureAllSelectedReportsHaveExecuted } from '../components/common/ExportReportButton';
import { IFeedbackMessage } from '../contexts/feedbackMessagesContext';
import { ISelectedTaxReturnsAndFilters } from '../data-types/exportReports';
import { WhitePaperReportExportRequest } from '../model';
import { getExportFileNameBasedOnSelectedReports } from '../utils/ReportUtils';

export const useExportConversations = (isReportLevelComments: boolean, getReportsToExport?: () => ISelectedTaxReturnsAndFilters) => {
    const [exportedReports, setExportedReports] = useState<WhitePaperReportExportRequest[]>();
    const [activeMessage, setActiveMessage] = useState<IFeedbackMessage | undefined>(undefined);
    const { t } = useTranslation();

    const saveFileAndClearState = (data: Blob, fileExtension: string, isTaxReturnList?: boolean) => {
        const fileName = `CommentsExport-${getExportFileNameBasedOnSelectedReports(exportedReports, fileExtension, t, isTaxReturnList)}`;

        FileSaver.saveAs(data, fileName);

        setActiveMessage(undefined);

        setExportedReports([]);
    };

    const { mutateAsync: exportConversationsToExcelMutationHelper, isLoading: isProcessing } = useExportConversationsForReportsToExcel({
        mutation: {
            onSuccess: (data: any) => {
                saveFileAndClearState(data as Blob, '.xlsx');
            },
            onError: () => {
                setActiveMessage({
                    message: t('commentsExportError').toString(),
                    type: 'error',
                });
            },
        },
    });

    const exportConversationsToExcel = async () => {
        setActiveMessage(undefined);

        const requests = getRequestsForExport(false);

        if (requests) {
            exportConversationsToExcelMutationHelper({ data: requests.selectedTaxReturns || [] });
        }
    };

    const getRequestsWithTimezone = (reports: ISelectedTaxReturnsAndFilters) => {
        return reports.selectedTaxReturns?.map((taxReturn) => {
            taxReturn.timezoneOffsetMinutes = new Date().getTimezoneOffset();
            return taxReturn;
        });
    };

    const getRequestsForExport = (validateExecutedReturns?: boolean): ISelectedTaxReturnsAndFilters | undefined => {
        try {
            const reports = getReportsToExport!();

            reports.selectedTaxReturns = reports.selectedTaxReturns?.filter((r) => r.canExecute !== false);

            reports.selectedTaxReturns = getRequestsWithTimezone(reports);

            ensureAllSelectedReportsHaveExecuted(reports, validateExecutedReturns, t('notExecutedReportSelectedForExportError'));

            setExportedReports(reports.selectedTaxReturns);

            return reports;
        } catch {
            setExportedReports([]);
            setActiveMessage({
                message: t('commentsExportError').toString(),
                type: 'error',
            });
            return undefined;
        }
    };

    if (!isReportLevelComments || !getReportsToExport) {
        return {
            exportConversationsToExcel: undefined,
        };
    }

    return {
        exportConversationsToExcel,
        activeMessage,
        clearActiveMessage: () => setActiveMessage(undefined),
        isProcessing,
    };
};
